/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { CommonActions, FormActionPipelines, FormFlowState } from '@gh/builder-shared';
import { ActionFlow, AppRouter, queryVersion, State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  DialogService,
  FormMode,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { of$ } from '@gh/rx';
import { concatWith$, filter$, map$, switchMap$ } from '@gh/rx/operators';
import { flatMap, isEmpty, sortBy } from 'lodash';
import { Observable } from 'rxjs';
import { PriceBookLinkBusinessService } from '../../resources/price-book-link-business.service';
import {
  isPbLinkCcNotPreloaded,
  PbLinkGetResponse,
  PbLinkMappingContext,
  PbLinkSaveResponse,
} from '../../resources/price-book-link.model';
import {
  PriceBookLinkCsmState,
  PriceBookLinkJstState,
  PriceBookLinkState,
  PriceBookLinkStateEvent,
  PriceBookLinkStateEventType,
  queryPbLinkCostCenter,
  queryPbLinkCsmHeadingBySubheading,
  queryPbLinkCsmSubheadingByItem,
  queryPbLinkEntryPoint,
  queryPbLinkFirstCostCenter,
  queryPbLinkHasErrors,
  queryPbLinkId,
  queryPbLinkJstParagraphBySection,
  queryPbLinkSaveModel,
} from './price-book-link.state';

export type PbLinkSaveFlowState = FormFlowState<PriceBookLinkState> & { saveResponse: PbLinkSaveResponse };

@Injectable()
export class PriceBookLinkCommandService {
  constructor(private state: State<PriceBookLinkState>,
              private waitings: WaitingService,
              private dialogs: DialogService,
              private translations: TranslationService,
              private notifications: NotificationService,
              private commonActions: CommonActions,
              private pipelines: FormActionPipelines,
              private business: PriceBookLinkBusinessService,
              private router: AppRouter) {
  }

  completeFlow(): ActionFlow<PbLinkSaveFlowState> {
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(
        () => this.state.project(queryPbLinkHasErrors),
        () => this.handleErrors()),
      execute: this.commonActions.async((state) => this.business.saveLinks(
        this.state.project(queryPbLinkEntryPoint),
        this.state.project(queryPbLinkId),
        this.state.project(queryPbLinkSaveModel),
        this.createPbLinkMappingContext()).pipe(map$((saveResponse) => ({ ...state, saveResponse })))),
      finish: ({ saveResponse }: PbLinkSaveFlowState) => {
        if (!isEmpty(saveResponse)) {
          this.notifications.warning('msg_pb_items_components_not_found', {
            notFoundList: this.buildNotFoundList(saveResponse),
          });
        }
        this.state.mutate({ type: PriceBookLinkStateEventType.ResetDirty });
        this.router.navigateBack();
      },
    });
  }

  load(init: PbLinkGetResponse, mode?: FormMode): void {
    this.state.mutate({ type: PriceBookLinkStateEventType.Load, init, mode });

    const cc = this.state.project(queryPbLinkFirstCostCenter);
    if (cc) {
      this.selectCostCenter(cc.id);
    }
  }

  setRecordField(recordId: ID, name: string, value: any): void {
    this.state.mutate({ type: PriceBookLinkStateEventType.SetRecordField, recordId, name, value });
  }

  selectCostCenter(costCenterId: ID): void {
    this.ensureCostCenterLoaded(costCenterId).pipe(
      concatWith$([{ type: PriceBookLinkStateEventType.SelectCostCenter, costCenterId }]))
      .subscribe(
        (event) => this.state.mutate(event),
        this.notifications.processAll);
  }

  selectElement(elementId: ID): void {
    this.state.mutate({ type: PriceBookLinkStateEventType.SelectElement, elementId });
  }

  resetRecordFocusedCell(): void {
    this.state.mutate({ type: PriceBookLinkStateEventType.ResetRecordFocusedCell });
  }

  reset(): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.business.getLinks(
        this.state.project(queryPbLinkEntryPoint),
        this.state.project(queryPbLinkId)).pipe(this.waitings.operator())))
      .subscribe(
        (init) => this.load(init),
        this.notifications.processAll,
      );
  }

  toggleShowDeleted(): void {
    this.state.mutate({ type: PriceBookLinkStateEventType.ToggleShowDeleted });
  }

  private ensureCostCenterLoaded(costCenterId: ID): Observable<PriceBookLinkStateEvent> {
    return of$(this.state.project((s) => queryPbLinkCostCenter(s, costCenterId))).pipe(
      filter$(Boolean),
      filter$(isPbLinkCcNotPreloaded),
      switchMap$(() => this.business.getCcElements(
        this.state.project(queryPbLinkEntryPoint),
        this.state.project(queryPbLinkId),
        costCenterId,
        this.state.project(queryVersion)!,
        this.createPbLinkMappingContext()).pipe(this.waitings.operator())),
      map$((elements) => <PriceBookLinkStateEvent>({
        type: PriceBookLinkStateEventType.LoadCostCenter,
        costCenterId,
        elements,
      })));
  }

  private handleErrors(): void {
    this.notifications.error('msg_he_incorrect_data_in_tables');

    this.state.mutate({ type: PriceBookLinkStateEventType.NavigateToInvalid });
  }

  private createPbLinkMappingContext(): PbLinkMappingContext {
    return {
      getParentJstParagraph: (sectionId) =>
        this.state.project((s: PriceBookLinkJstState) => queryPbLinkJstParagraphBySection(s, sectionId)),
      getCostCenter: (costCenterId) => this.state.project((s) => queryPbLinkCostCenter(s, costCenterId))!,
      getParentCsmSubheading: (itemId) =>
        this.state.project((s: PriceBookLinkCsmState) => queryPbLinkCsmSubheadingByItem(s, itemId)),
      getParentCsmHeading: (subheadingId) =>
        this.state.project((s: PriceBookLinkCsmState) => queryPbLinkCsmHeadingBySubheading(s, subheadingId)),
    };
  }

  private buildNotFoundList(saveResponse: PbLinkSaveResponse): string {
    return flatMap(sortBy(Object.keys(saveResponse)), (cc) => saveResponse[cc].map((itemCode) =>
      `${this.translations.get('lbl_cc_number')} ${cc}: ${itemCode}`)).join('\n');
  }
}
